import React, { useState } from 'react';
import { Home, MapPin, Users, DollarSign, Star, TrendingUp, Calendar, Search, Download, Plus, Eye, Filter, Grid, List, BarChart3 } from 'lucide-react';
import VillaDetail from './VillaDetail';

const VillasManagement = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [selectedDestination, setSelectedDestination] = useState('all');
  const [selectedAvailability, setSelectedAvailability] = useState('all');
  const [selectedCapacity, setSelectedCapacity] = useState('all');
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVilla, setSelectedVilla] = useState(null);
  const [showVillaDetail, setShowVillaDetail] = useState(false);

  // Statistiques principales
  const stats = {
    villasActives: 247,
    prixMoyenSemaine: 185000,
    tauxOccupation: 87,
    noteMoyenne: 4.9,
    revenueYTD: 42000000,
    nouvellesCeMois: 18
  };

  // Destinations
  const destinations = [
    { id: 'all', name: 'Toutes destinations', count: 247 },
    { id: 'st-barth', name: 'Saint-Barthélemy', count: 68 },
    { id: 'mustique', name: 'Mustique', count: 24 },
    { id: 'anguilla', name: 'Anguilla', count: 31 },
    { id: 'st-tropez', name: 'Saint-Tropez', count: 45 },
    { id: 'cannes', name: 'Cannes', count: 32 },
    { id: 'ibiza', name: 'Ibiza', count: 18 },
    { id: 'mykonos', name: 'Mykonos', count: 29 },
    { id: 'sardaigne', name: 'Sardaigne', count: 16 }
  ];

  // Villas principales
  const villas = [
    {
      id: 'VIL001',
      name: 'Villa Azure Dreams',
      destination: 'Saint-Barthélemy',
      location: 'Gustavia',
      bedrooms: 6,
      bathrooms: 8,
      maxGuests: 12,
      features: ['Piscine', 'Vue mer', 'Chef'],
      category: 'Ultra Luxury',
      pricePerWeek: 280000,
      occupancy: 92,
      rating: 4.9,
      revenueYTD: 2400000,
      status: 'active',
      image: 'azure-dreams.jpg',
      specialFeatures: ['Piscine', 'Chef inclus', 'Plage privée'],
      source: 'Direct',
      gpsCoordinates: '17.8962°N, 62.8498°W',
      gateCode: 'AZ2024*',
      wifiCode: 'AzureDreams2024!',
      parkingCode: '1234',
      safeCode: '9876'
    },
    {
      id: 'VIL002',
      name: 'Villa Sunset Paradise',
      destination: 'Saint-Barthélemy',
      location: 'Shell Beach',
      bedrooms: 8,
      bathrooms: 10,
      maxGuests: 16,
      features: ['Piscine', 'Vue mer', 'Tennis'],
      category: 'Ultra Luxury',
      pricePerWeek: 350000,
      occupancy: 88,
      rating: 4.8,
      revenueYTD: 3200000,
      status: 'active',
      image: 'sunset-paradise.jpg',
      specialFeatures: ['Court de tennis', 'Spa privé', 'Héliport'],
      source: 'VRBO'
    },
    {
      id: 'VIL003',
      name: 'Villa Ocean Breeze',
      destination: 'Mustique',
      location: 'Cotton Bay',
      bedrooms: 5,
      bathrooms: 6,
      maxGuests: 10,
      features: ['Piscine', 'Vue mer', 'Jardin'],
      category: 'Luxury',
      pricePerWeek: 180000,
      occupancy: 85,
      rating: 4.7,
      revenueYTD: 1800000,
      status: 'active',
      image: 'ocean-breeze.jpg',
      specialFeatures: ['Jardin tropical', 'Plage privée'],
      source: 'Airbnb'
    }
  ];

  // Calendrier d'occupation fictif
  const occupancyCalendar = [
    { date: 1, villa: 'VIL001' },
    { date: 2, villa: null },
    { date: 3, villa: 'VIL002' },
    { date: 4, villa: 'VIL002' },
    { date: 5, villa: 'VIL001' },
    { date: 6, villa: 'VIL001' },
    { date: 7, villa: 'VIL003' },
    { date: 8, villa: null },
    { date: 9, villa: null },
    { date: 10, villa: 'VIL002' }
  ];

  // Helper functions
  const getCategoryColor = (category) => {
    const colors = {
      'Luxury': 'bg-purple-100 text-purple-800',
      'Ultra Luxury': 'bg-yellow-100 text-yellow-800',
      'Premium': 'bg-blue-100 text-blue-800',
      'Standard': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getSourceColor = (source) => {
    const colors = {
      'Direct': 'bg-green-100 text-green-800',
      'Airbnb': 'bg-red-100 text-red-800',
      'VRBO': 'bg-blue-100 text-blue-800',
      'Booking.com': 'bg-purple-100 text-purple-800'
    };
    return colors[source] || 'bg-gray-100 text-gray-800';
  };

  // Components
  const VillaCard = ({ villa }) => (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
      {/* Image placeholder */}
      <div className="h-48 bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center relative">
        <div className="absolute top-4 right-4">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(villa.category)}`}>
            {villa.category}
          </span>
        </div>
        <h3 className="text-white text-xl font-bold text-center px-4">{villa.name}</h3>
      </div>

      <div className="p-6">
        <div className="flex items-center space-x-1 text-gray-600 text-sm mb-3">
          <MapPin size={14} />
          <span>{villa.destination}, {villa.location}</span>
        </div>

        {/* Villa specs */}
        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
          <div className="flex items-center space-x-1">
            <span>🛏️</span>
            <span>{villa.bedrooms} Chambres</span>
          </div>
          <div className="flex items-center space-x-1">
            <span>🚿</span>
            <span>{villa.bathrooms} SDB</span>
          </div>
          <div className="flex items-center space-x-1">
            <span>👥</span>
            <span>{villa.maxGuests} Pers.</span>
          </div>
        </div>

        {/* Special features */}
        <div className="flex flex-wrap gap-2 mb-4">
          {villa.specialFeatures.map((feature, index) => (
            <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
              {feature}
            </span>
          ))}
        </div>

        {/* Pricing and stats */}
        <div className="space-y-2 mb-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Haute Saison</span>
            <span className="font-bold text-lg text-gray-800">€{(villa.pricePerWeek / 1000).toFixed(0)}K/sem</span>
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-center text-sm">
            <div>
              <div className="font-bold text-gray-800">{villa.occupancy}%</div>
              <div className="text-gray-600">Occupation</div>
            </div>
            <div>
              <div className="font-bold text-gray-800">{villa.rating}</div>
              <div className="text-gray-600">Note</div>
            </div>
            <div>
              <div className="font-bold text-gray-800">€{(villa.revenueYTD / 1000000).toFixed(1)}M</div>
              <div className="text-gray-600">Rev. YTD</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-2">
          <button 
            className="flex-1 px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded hover:bg-blue-50"
            onClick={() => {
              setSelectedVilla(villa);
              setShowVillaDetail(true);
            }}
          >
            Voir Détails
          </button>
          <button className="flex-1 px-3 py-2 text-sm text-green-600 border border-green-300 rounded hover:bg-green-50">
            Calendrier
          </button>
          <button className="flex-1 px-3 py-2 text-sm text-purple-600 border border-purple-300 rounded hover:bg-purple-50">
            Réserver
          </button>
        </div>
      </div>
    </div>
  );

  const VillaRow = ({ villa }) => (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4">
        <div className="w-10 h-10 bg-yellow-500 rounded flex items-center justify-center">
          <Home size={16} className="text-white" />
        </div>
      </td>
      <td className="px-6 py-4">
        <div>
          <div 
            className="font-semibold text-gray-800 cursor-pointer hover:text-yellow-600"
            onClick={() => {
              setSelectedVilla(villa);
              setShowVillaDetail(true);
            }}
          >
            {villa.name}
          </div>
          <div className="text-sm text-gray-600">#{villa.id}</div>
        </div>
      </td>
      <td className="px-6 py-4 text-gray-900">{villa.destination}</td>
      <td className="px-6 py-4">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSourceColor(villa.source)}`}>
          {villa.source}
        </span>
      </td>
      <td className="px-6 py-4 text-gray-900">{villa.maxGuests} personnes</td>
      <td className="px-6 py-4 font-medium text-gray-900">€{(villa.pricePerWeek / 1000).toFixed(0)}K</td>
      <td className="px-6 py-4 text-gray-900">{villa.occupancy}%</td>
      <td className="px-6 py-4 text-gray-900">⭐ {villa.rating}</td>
      <td className="px-6 py-4 font-medium text-green-600">€{(villa.revenueYTD / 1000000).toFixed(1)}M</td>
      <td className="px-6 py-4">
        <button 
          className="text-blue-600 hover:text-blue-900 text-sm font-medium"
          onClick={() => {
            setSelectedVilla(villa);
            setShowVillaDetail(true);
          }}
        >
          Gérer
        </button>
      </td>
    </tr>
  );

  // Filtering logic
  const filteredVillas = villas.filter(villa => {
    const matchesDestination = selectedDestination === 'all' || villa.destination.toLowerCase().includes(selectedDestination.replace('-', ' '));
    const matchesSearch = villa.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         villa.destination.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCapacity = selectedCapacity === 'all' || 
                           (selectedCapacity === '2-4' && villa.maxGuests <= 4) ||
                           (selectedCapacity === '4-8' && villa.maxGuests > 4 && villa.maxGuests <= 8) ||
                           (selectedCapacity === '8-12' && villa.maxGuests > 8 && villa.maxGuests <= 12) ||
                           (selectedCapacity === '12+' && villa.maxGuests > 12);
    const matchesPriceRange = selectedPriceRange === 'all' ||
                             (selectedPriceRange === '<50K' && villa.pricePerWeek < 50000) ||
                             (selectedPriceRange === '50K-100K' && villa.pricePerWeek >= 50000 && villa.pricePerWeek <= 100000) ||
                             (selectedPriceRange === '100K-200K' && villa.pricePerWeek > 100000 && villa.pricePerWeek <= 200000) ||
                             (selectedPriceRange === '200K+' && villa.pricePerWeek > 200000);
    
    return matchesDestination && matchesSearch && matchesCapacity && matchesPriceRange;
  });

  // Affichage conditionnel du détail de villa
  if (showVillaDetail && selectedVilla) {
    return (
      <VillaDetail 
        villa={selectedVilla} 
        onBack={() => {
          setShowVillaDetail(false);
          setSelectedVilla(null);
        }} 
      />
    );
  }

  return (
    <div className="villas-management">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Gestion des Villas</h1>
            <p className="text-gray-600 mt-1">Vue d'ensemble de votre portfolio de villas de luxe</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <div className="flex items-center border border-gray-300 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-yellow-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 ${viewMode === 'list' ? 'bg-yellow-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <List size={16} />
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-2 ${viewMode === 'calendar' ? 'bg-yellow-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <Calendar size={16} />
              </button>
            </div>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Download size={18} />
              <span>Export</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Plus size={18} />
              <span>Ajouter Villa</span>
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-800">{stats.villasActives}</div>
            <div className="text-sm text-gray-600">Villas Actives</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-800">€{(stats.prixMoyenSemaine / 1000).toFixed(0)}K</div>
            <div className="text-sm text-gray-600">Prix Moyen/Semaine</div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-800">{stats.tauxOccupation}%</div>
            <div className="text-sm text-gray-600">Taux d'Occupation</div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-800">{stats.noteMoyenne}</div>
            <div className="text-sm text-gray-600">Note Moyenne</div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-800">€{(stats.revenueYTD / 1000000).toFixed(0)}M</div>
            <div className="text-sm text-gray-600">Revenue YTD</div>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-800">{stats.nouvellesCeMois}</div>
            <div className="text-sm text-gray-600">Nouvelles ce Mois</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Filtres */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <select
              value={selectedDestination}
              onChange={(e) => setSelectedDestination(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              {destinations.map((dest) => (
                <option key={dest.id} value={dest.id}>{dest.name}</option>
              ))}
            </select>

            <select
              value={selectedAvailability}
              onChange={(e) => setSelectedAvailability(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Toutes</option>
              <option value="available">Disponible</option>
              <option value="occupied">Occupée</option>
              <option value="maintenance">Maintenance</option>
            </select>

            <select
              value={selectedCapacity}
              onChange={(e) => setSelectedCapacity(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous</option>
              <option value="2-4">2-4 personnes</option>
              <option value="4-8">4-8 personnes</option>
              <option value="8-12">8-12 personnes</option>
              <option value="12+">12+ personnes</option>
            </select>

            <select
              value={selectedPriceRange}
              onChange={(e) => setSelectedPriceRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous prix</option>
              <option value="<50K">{'< €50K'}</option>
              <option value="50K-100K">€50K - €100K</option>
              <option value="100K-200K">€100K - €200K</option>
              <option value="200K+">€200K+</option>
            </select>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Recherche"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>

            <button className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Réinitialiser
            </button>
          </div>
        </div>

        {/* Contenu principal */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredVillas.map((villa) => (
              <VillaCard key={villa.id} villa={villa} />
            ))}
          </div>
        )}

        {viewMode === 'list' && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Photo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom / ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Destination</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Capacité</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix/Sem</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Occupation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Note</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredVillas.map((villa) => (
                  <VillaRow key={villa.id} villa={villa} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'calendar' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800">Calendrier d'Occupation - Septembre 2025</h3>
              <div className="flex items-center space-x-4">
                <button className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50">← Août</button>
                <button className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50">Octobre →</button>
              </div>
            </div>
            
            <div className="space-y-4">
              {villas.map((villa) => (
                <div key={villa.id} className="flex items-center space-x-4">
                  <div className="w-32 font-medium text-gray-800">{villa.name}</div>
                  <div className="flex-1 grid grid-cols-10 gap-1">
                    {occupancyCalendar.slice(0, 10).map((day, index) => (
                      <div
                        key={index}
                        className={`h-8 border rounded text-xs flex items-center justify-center ${
                          day.villa 
                            ? 'bg-yellow-100 border-yellow-300 text-yellow-800' 
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        {day.date}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 space-y-2">
              <div className="text-sm font-medium text-gray-700">Réservations actives :</div>
              <div className="text-sm text-yellow-800">5-6 : J. Davidson - €280K</div>
              <div className="text-sm text-yellow-800">7 : S. Chen - €175K</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VillasManagement;